// Material Dashboard 2 React layouts
import FDOPanel from "layouts/fdo-panel";
import Dashboard from "layouts/dashboard";
import Tables from "layouts/tables";
import Revenue from "layouts/Revenue";
import RTL from "layouts/rtl";
import SecurityDeposit from "layouts/security-deposit";
import SecurityDepositRefund from "layouts/security-deposit-refund";

import SignIn from "layouts/authentication/sign-in";
import ForgotPassword from "layouts/forgot-password";
import ResetPassword from "layouts/reset-password";
import AdminPanel from "layouts/admin-panel";
import Payments from "layouts/payments";
import QBPayment from "layouts/qb-payment";
import Inventory from "layouts/inventory";
import CommentSection from "components/CommentSection/CommentSection";
import PasscodeAccess from "pages/PasscodeAccess";
import Cleaning from "layouts/cleaning";
import CleaningStatus from "layouts/cleaning-status";
import Rooms from "layouts/Rooms";
import Upsells from "layouts/upsells";
// @mui icons
import Icon from "@mui/material/Icon";

// Role-based route filtering
export const getRoleBasedRoutes = (userRole, isAuthenticated, userPermissions = null, username = null) => {
  // Hidden pages list (Inventory and Revenue)
  const hiddenKeys = ["inventory", "revenue"];

  // If not authenticated, only show sign-in and forgot password
  if (!isAuthenticated) {
    const filteredRoutes = routes.filter((route) => (route.key === "sign-in" || route.key === "forgot-password" || route.key === "reset-password") && !hiddenKeys.includes(route.key));
    return filteredRoutes;
  }

  // If user role, show FDO Panel, QB Payments, Upsells, and its sub-pages
  if (userRole === "user") {
    const userKeys = ["fdo-panel", "qb-payments", "upsells", "hostaway-reservation", "upsell-list", "charges-list"];
    const filteredRoutes = routes.filter((route) => userKeys.includes(route.key) && !hiddenKeys.includes(route.key));
    return filteredRoutes;
  }

  // If admin role, show all routes except sign-in and hidden pages
  if (userRole === "admin") {
    const filteredRoutes = routes.filter((route) => route.key !== "sign-in" && !hiddenKeys.includes(route.key));
    return filteredRoutes;
  }

  // If view_only (Full Access) role, show all routes except sign-in, admin-panel, and hidden pages
  if (userRole === "view_only") {
    const filteredRoutes = routes.filter((route) => route.key !== "sign-in" && route.key !== "admin-panel" && !hiddenKeys.includes(route.key));
    return filteredRoutes;
  }


  // If custom role, show routes based on permissions
  if (userRole === "custom") {
    let allowedRoutes = [];

    // Add routes based on permissions
    let permissions = null;

    // Try to get permissions from parameter
    if (userPermissions) {
      try {
        permissions = typeof userPermissions === 'string'
          ? JSON.parse(userPermissions)
          : userPermissions;
      } catch (error) {
        // Silent error handling
      }
    }

    // Fallback to localStorage if no permissions and username provided
    if (!permissions && username) {
      try {
        const storedPermissions = localStorage.getItem(`permissions_${username}`);
        if (storedPermissions) {
          permissions = JSON.parse(storedPermissions);
        }
      } catch (error) {
        // Silent error handling
      }
    }

    if (permissions) {
      try {
        const orderedKeys = ["fdo-panel", "upsells", "revenue", "payments", "qb-payments", "cleaning", "cleaning-status", "rooms", "inventory", "security-deposit", "security-deposit-refund", "admin-panel", "forgot-password", "logout"];

        for (const key of orderedKeys) {
          switch (key) {
            case "fdo-panel":
              if (permissions?.fdoPanel?.view || permissions?.fdoPanel?.complete) {
                allowedRoutes.push(...routes.filter(route => route.key === "fdo-panel"));
              }
              break;

            case "revenue":
              if (permissions?.revenue?.view || permissions?.revenue?.complete) {
                allowedRoutes.push(...routes.filter(route => route.key === "revenue"));
              }
              break;
            case "payments":
              if (permissions?.payment?.view || permissions?.payment?.complete) {
                allowedRoutes.push(...routes.filter(route => route.key === "payments"));
              }
              break;
            case "qb-payments":
              if (permissions?.qbPayment?.view || permissions?.qbPayment?.complete) {
                allowedRoutes.push(...routes.filter(route => route.key === "qb-payments"));
              }
              break;
            case "cleaning":
              if (permissions?.cleaning?.view || permissions?.cleaning?.complete) {
                allowedRoutes.push(...routes.filter(route => route.key === "cleaning"));
              }
              break;
            case "cleaning-status":
              if (permissions?.cleaning?.view || permissions?.cleaning?.complete) {
                allowedRoutes.push(...routes.filter(route => route.key === "cleaning-status"));
              }
              break;
            case "rooms":
              if (permissions?.rooms?.view || permissions?.rooms?.complete || permissions?.cleaning?.view || permissions?.cleaning?.complete) {
                allowedRoutes.push(...routes.filter(route => route.key === "rooms"));
              }
              break;
            case "inventory":
              if (permissions?.inventory?.view || permissions?.inventory?.complete) {
                allowedRoutes.push(...routes.filter(route => route.key === "inventory"));
              }
              break;
            case "security-deposit":
              if (permissions?.securityDeposit?.view || permissions?.securityDeposit?.complete) {
                allowedRoutes.push(...routes.filter(route => route.key === "security-deposit"));
              }
              break;
            case "security-deposit-refund":
              if (permissions?.securityDeposit?.view || permissions?.securityDeposit?.complete) {
                allowedRoutes.push(...routes.filter(route => route.key === "security-deposit-refund"));
              }
              break;
            case "upsells":
              if (permissions?.upsells?.view || permissions?.upsells?.complete) {
                allowedRoutes.push(...routes.filter(route => route.key === "upsells" || route.key === "hostaway-reservation" || route.key === "upsell-list" || route.key === "charges-list"));
              }
              break;
            case "admin-panel":
              if (permissions?.userManagement?.complete ||
                permissions?.passwordHistory?.complete ||
                permissions?.monthlyTarget?.view || permissions?.monthlyTarget?.complete) {
                allowedRoutes.push(...routes.filter(route => route.key === "admin-panel"));
              }
              break;
            case "forgot-password":
              if (permissions?.resetPassword) {
                allowedRoutes.push(...routes.filter(route => route.key === "forgot-password"));
              }
              break;
            case "logout":
              // Always add logout for custom users
              allowedRoutes.push(...routes.filter(route => route.key === "logout"));
              break;
            default:
              break;
          }
        }
      } catch (error) {
        // Silent error handling
      }
    }

    // Remove any duplicates that might have been added and filter out hidden keys
    const uniqueRoutes = allowedRoutes.filter((route, index, self) =>
      index === self.findIndex(r => r.key === route.key) && !hiddenKeys.includes(route.key)
    );
    return uniqueRoutes;
  }

  // Default fallback for authenticated users: show all routes except sign-in and hidden pages
  if (isAuthenticated) {
    const filteredRoutes = routes.filter((route) => route.key !== "sign-in" && !hiddenKeys.includes(route.key));
    return filteredRoutes;
  }

  // Default: only show sign-in
  const filteredRoutes = routes.filter((route) => route.key === "sign-in");
  return filteredRoutes;
};

// All available routes - Ordered according to desired sidebar pattern
const routes = [
  // 1. FDO Panel
  {
    type: "collapse",
    name: "FDO Panel",
    key: "fdo-panel",
    icon: <Icon fontSize="small">notifications</Icon>,
    route: "/fdo-panel",
    component: <FDOPanel />,
  },

  // 2. Upsells (Position 2 in Sidebar)
  {
    type: "collapse",
    name: "Add Upsells / Charges",
    key: "upsells",
    icon: <Icon fontSize="small">local_offer</Icon>,
    route: "/upsells",
    component: <Upsells />,
  },
  {
    type: "hidden",
    name: "Hostaway Reservation",
    key: "hostaway-reservation",
    icon: <Icon fontSize="small">person_search</Icon>,
    route: "/hostaway-reservation",
    component: <Upsells defaultTab={0} />,
  },
  {
    type: "hidden",
    name: "Upsell List",
    key: "upsell-list",
    icon: <Icon fontSize="small">local_offer</Icon>,
    route: "/upsell-list",
    component: <Upsells defaultTab={1} />,
  },
  {
    type: "hidden",
    name: "Charges List",
    key: "charges-list",
    icon: <Icon fontSize="small">account_balance_wallet</Icon>,
    route: "/charges-list",
    component: <Upsells defaultTab={2} />,
  },

  // 3. Revenue
  {
    type: "hidden",
    name: "Revenue",
    key: "revenue",
    icon: <Icon fontSize="small">receipt_long</Icon>,
    route: "/revenue",
    component: <Revenue />,
  },

  // 3. Payments
  {
    type: "collapse",
    name: "Payments",
    key: "payments",
    icon: <Icon fontSize="small">payment</Icon>,
    route: "/payments",
    component: <Payments />,
  },

  // 3b. QB Payments
  {
    type: "collapse",
    name: "QB Payments",
    key: "qb-payments",
    icon: <Icon fontSize="small">account_balance_wallet</Icon>,
    route: "/qb-payments",
    component: <QBPayment />,
  },


  // 5. Cleaning
  {
    type: "collapse",
    name: "Cleaning",
    key: "cleaning",
    icon: <Icon fontSize="small">cleaning_services</Icon>,
    route: "/cleaning",
    component: <Cleaning />,
  },

  // 5b. Cleaning Status (hidden from sidebar - accessed via tab bar on Cleaning page)
  {
    type: "hidden",
    name: "Cleaning status",
    key: "cleaning-status",
    icon: <Icon fontSize="small">cleaning_services</Icon>,
    route: "/cleaning-status",
    component: <CleaningStatus />,
  },

  // 5c. Rooms
  {
    type: "hidden",
    name: "Rooms",
    key: "rooms",
    icon: <Icon fontSize="small">meeting_room</Icon>,
    route: "/rooms",
    component: <Rooms />,
  },

  // 6. Inventory
  {
    type: "hidden",
    name: "Inventory",
    key: "inventory",
    icon: <Icon fontSize="small">inventory_2</Icon>,
    route: "/inventory",
    component: <Inventory />,
  },

  // 7. Security Deposit
  {
    type: "collapse",
    name: "Security Deposit",
    key: "security-deposit",
    icon: <Icon fontSize="small">security</Icon>,
    route: "/security-deposit",
    component: <SecurityDeposit />,
  },

  // 7b. Security Deposit Refund
  {
    type: "collapse",
    name: "SD Refund",
    key: "security-deposit-refund",
    icon: <Icon fontSize="small">account_balance</Icon>,
    route: "/security-deposit-refund",
    component: <SecurityDepositRefund />,
  },

  // 8. Admin Panel
  {
    type: "collapse",
    name: "Admin Panel",
    key: "admin-panel",
    icon: <Icon fontSize="small">admin_panel_settings</Icon>,
    route: "/admin-panel",
    component: <AdminPanel />,
  },

  // 9. Reset Password
  {
    type: "collapse",
    name: "Reset Password",
    key: "forgot-password",
    icon: <Icon fontSize="small">lock_reset</Icon>,
    route: "/forgot-password",
    component: <ForgotPassword />,
  },

  // 10. Logout
  {
    type: "collapse",
    name: "Logout",
    key: "logout",
    icon: <Icon fontSize="small">logout</Icon>,
    route: "/logout",
    component: null, // This will be handled by the sidebar component
  },

  // Hidden routes (not shown in sidebar)
  {
    type: "hidden",
    name: "Dashboard",
    key: "dashboard",
    icon: <Icon fontSize="small">dashboard</Icon>,
    route: "/dashboard",
    component: <Dashboard />,
  },
  {
    type: "hidden",
    name: "Tables",
    key: "tables",
    icon: <Icon fontSize="small">table_view</Icon>,
    route: "/tables",
    component: <Tables />,
  },
  {
    type: "hidden",
    name: "RTL",
    key: "rtl",
    icon: <Icon fontSize="small">format_textdirection_r_to_l</Icon>,
    route: "/rtl",
    component: <RTL />,
  },
  {
    type: "hidden", // or "collapse" if you want it visible in sidebar
    name: "Comment Section",
    key: "comment-section",
    icon: <Icon fontSize="small">comment</Icon>,
    route: "/comment-section",
    component: <CommentSection />,
  },
  {
    type: "hidden",
    name: "Passcode Access",
    key: "passcode-access",
    icon: <Icon fontSize="small">lock</Icon>,
    route: "/passcode-access",
    component: <PasscodeAccess />,
  },
  {
    type: "collapse",
    name: "Sign In",
    key: "sign-in",
    icon: <Icon fontSize="small">login</Icon>,
    route: "/authentication/sign-in",
    component: <SignIn />,
  },
  {
    type: "hidden",
    name: "Reset Password",
    key: "reset-password",
    route: "/authentication/reset-password",
    component: <ResetPassword />,
  },
];

// Export individual route components for direct access
export { FDOPanel, SignIn, ForgotPassword };

export default routes;
